import type { MouseEvent, ReactNode } from "react";
import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { getPublicNavigationHref, type NavigationDestination } from "@/lib/navigationConfig";
import { ExternalLink } from "@/shared/ui/external-link";
import { InternalLink } from "@/shared/ui/internal-link";
import { preparePublicNavigationFocus } from "./focusPublicMain";

type PublicNavigationLinkProps = {
  link: NavigationDestination;
  pathname: string;
  analyticsElementId: AnalyticsElementId;
  onNavigate?: (href: string) => void;
  className?: string;
  children?: ReactNode;
};

export function PublicNavigationLink({
  link,
  pathname,
  analyticsElementId,
  onNavigate,
  className,
  children,
}: PublicNavigationLinkProps) {
  const href = getPublicNavigationHref(link, pathname);
  const navigate = (event: MouseEvent) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    preparePublicNavigationFocus(href);
    onNavigate?.(href);
  };
  return href.startsWith("#") ? (
    <ExternalLink
      analyticsElementId={analyticsElementId}
      href={href}
      onClick={navigate}
      className={className}
      aria-label={link.label}
    >
      {children ?? link.label}
    </ExternalLink>
  ) : (
    <InternalLink
      analyticsElementId={analyticsElementId}
      href={href}
      scrollToTop={link.kind !== "section"}
      className={className}
      aria-label={link.label}
      onClick={navigate}
    >
      {children ?? link.label}
    </InternalLink>
  );
}
